import { useState, useMemo, useEffect } from "react";
import { LuBuilding2, LuHome, LuBriefcase, LuBedDouble, LuMapPin, LuPencil, LuList } from "react-icons/lu";
import { TbX, TbPlus } from "react-icons/tb";
import { CustomerAddress } from "data-types";
import PortaledModal from "../../../../components/Modals/PortaledModal";
import * as translator from "../../../../utils/translator";
import { useShippingPageContext } from "../providers/ShippingPageProvider";
import { useBottomSheetAnimation } from "../hooks/useBottomSheetAnimation";
import "../styles/CustomerAddressesModal.css";

const ADDRESS_SUB_TYPE_ICONS: Record<string, React.ReactElement> = {
  apartment: <LuBuilding2 size={24} />,
  house:     <LuHome size={24} />,
  office:    <LuBriefcase size={24} />,
  hotel:     <LuBedDouble size={24} />,
  other:     <LuMapPin size={24} />,
};

const MAX_COLLAPSED_ADDRESSES = 3;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  addresses: CustomerAddress[];
  defaultAddressId?: number;
  title: string;
  addAddressLabel: string;
  onSelectAddress: (address: CustomerAddress) => void;
  onAddNewAddress: () => void;
  onEditAddress: (address: CustomerAddress) => void;
  isMobile?: boolean;
};

const CustomerAddressesModal: React.FC<Props> = ({ isOpen, onClose, addresses, defaultAddressId, title, addAddressLabel, onSelectAddress, onAddNewAddress, onEditAddress, isMobile = false }) => {
  const { language } = useShippingPageContext();
  const [showAll, setShowAll] = useState(false);
  const hasMoreThanMax = addresses.length > MAX_COLLAPSED_ADDRESSES;
  const { shouldRender, isClosing } = useBottomSheetAnimation(isOpen, isMobile);

  useEffect(() => {
    if (isOpen) setShowAll(false);
  }, [isOpen]);

  const visibleAddresses = useMemo(() => {
    if (!isMobile || showAll || !hasMoreThanMax) return addresses;

    const defaultAddr = addresses.find((a) => a.id === defaultAddressId);
    const rest = addresses
      .filter((a) => a.id !== defaultAddressId)
      .sort((a, b) => b.id - a.id)
      .slice(0, defaultAddr ? 2 : 3);

    return defaultAddr ? [defaultAddr, ...rest] : rest;
  }, [addresses, defaultAddressId, showAll, hasMoreThanMax, isMobile]);

  const rootClasses = [
    "customer-addresses-modal",
    isMobile ? "customer-addresses-modal--mobile" : "customer-addresses-modal--desktop",
    isMobile ? (isClosing ? "animate-bottom-sheet-out" : "animate-bottom-sheet-in") : "",
  ].filter(Boolean).join(" ");

  const headerClasses = [
    "customer-addresses-modal__header",
    isMobile ? "customer-addresses-modal__header--mobile" : "",
  ].filter(Boolean).join(" ");

  return (
    <PortaledModal
      shouldShowModal={shouldRender}
      hideModal={onClose}
      shouldBackgroundClickHideModal={true}
    >
      <div id="customer-addresses-modal" className={rootClasses}>

        {/* Header */}
        <div className={headerClasses}>
          <span className="customer-addresses-modal__title">
            {title}
          </span>
          <button onClick={onClose} className="customer-addresses-modal__close">
            <TbX size={20} color="#333" />
          </button>
        </div>

        {/* Address list */}
        <div className="customer-addresses-modal__list">
          {visibleAddresses.map((addr) => {
            const icon = ADDRESS_SUB_TYPE_ICONS[addr.address_sub_type || "apartment"] ?? ADDRESS_SUB_TYPE_ICONS.apartment;
            return (
              <div key={addr.id} className="customer-addresses-modal__item">
                <div
                  onClick={() => { onSelectAddress(addr); onClose(); }}
                  className="customer-addresses-modal__item-info"
                >
                  <span className="customer-addresses-modal__item-icon">
                    {icon}
                  </span>
                  <div className="customer-addresses-modal__item-text">
                    <span className="customer-addresses-modal__item-address">
                      {addr.address}
                    </span>
                    <span className="customer-addresses-modal__item-city">
                      {addr.city}{addr.phone ? ` - ${addr.phone}` : ""}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => onEditAddress(addr)}
                  className="customer-addresses-modal__item-edit"
                >
                  <LuPencil size={20} color="#98a0af" />
                </button>
              </div>
            );
          })}

          {/* "All addresses" button — only on mobile when collapsed */}
          {isMobile && hasMoreThanMax && !showAll && (
            <button onClick={() => setShowAll(true)} className="customer-addresses-modal__all-btn">
              <LuList size={16} color="#616161" />
              <span className="customer-addresses-modal__all-btn-label">
                {translator.translate(language, "shipping", "CustomerAddressesModal/all_addresses") || "All addresses"}
              </span>
            </button>
          )}
        </div>

        {/* Footer — Add address button */}
        <div className="customer-addresses-modal__footer">
          <button onClick={onAddNewAddress} className="customer-addresses-modal__add-btn">
            <TbPlus size={24} />
            {addAddressLabel}
          </button>
        </div>
      </div>
    </PortaledModal>
  );
};

export default CustomerAddressesModal;
